import { useMemo } from "react"
import { NO_VALUE, money, ratio, safeDivide, windowTotals } from "../../../performanceSnapshot"
import { formatMargin } from "../../../marginTrend"

/*
    Full Campaign and Current Billing Period panels (MPN-1530 §4.1, §4.2, §4.3).

    The Full Campaign panel deliberately ignores the Date Filter: §4.1 defines Total Spend
    to Date as the whole flight, and a "budget remaining" that grew when you narrowed the
    date range would be actively misleading.

    §4.3's two per-user metrics DO follow the filter for spend, because "what did this
    month cost per student" is the question, and divide by the current matched count,
    which is not windowed (§5.2). Both halves of that are deliberate.

    The billing panel is omitted from the DOM entirely when the contract has no amount and
    frequency, as the ticket requires -- not rendered blank.
*/

function Kpi({ label, value, note, negative }: {
  label: string
  value: string
  note?: string
  negative?: boolean
}) {
  return (
    <div className="mon-kpi">
      <div className={`mon-kpi__value${negative ? " mon-kpi__value--neg" : ""}`}>{value}</div>
      <div className="mon-kpi__label">
        {label}
        {note && <span className="mon-kpi__note">{note}</span>}
      </div>
    </div>
  )
}

export default function SpendPanels({ data }: { data: any }) {
  const panels = data?.["spend-panels"] ?? {}
  const period = panels.billing_period ?? null

  /*
      Impressions per User replaces Spend per Impression (MPN-1548). Spend/Impression
      was CPM by another name, to three more decimal places and without the thousand
      that makes it readable -- the CPM tile on the Conversions tab already answers it.
      Impressions per User answers a question nothing else did: how often did we
      actually put an ad in front of each person we were paying to reach.
  */
  const perUser = useMemo(() => {
    const { spend, impressions, selected } = windowTotals(data)
    return {
      spendPerUser: safeDivide(spend, selected),
      impressionsPerUser: safeDivide(impressions, selected),
    }
  }, [data])

  const hasCampaign = panels.total_campaign_budget !== undefined

  return (
    <>
      {hasCampaign && (
        <div className="mon-panel">
          <h4 className="mon-panel__head">
            Full Campaign
            {panels.days_scheduled > 0 && (
              <span className="mon-panel__tag">
                entire flight, {panels.days_live} of {panels.days_scheduled} days
              </span>
            )}
          </h4>
          <div className="mon-panel__cards">
            <Kpi label="Total Spend to Date" value={money(panels.total_spend_to_date ?? 0)} />
            <Kpi label="Total Campaign Budget" value={money(panels.total_campaign_budget ?? 0)} />
            <Kpi
              label="Campaign Budget Remaining"
              value={money(panels.campaign_budget_remaining ?? 0)}
              negative={(panels.campaign_budget_remaining ?? 0) < 0}
            />
            <Kpi
              label="Margin"
              value={formatMargin(panels.margin ?? null)}
              note="see trend below"
              negative={(panels.margin ?? 0) < 0}
            />
          </div>
          <p className="mon-panel__foot">
            Budget is the campaign's full-flight allocation from the Budget page. Margin is
            the prorated budget to date less actual spend, over the prorated budget.
          </p>
        </div>
      )}

      {/*
          Omitted entirely, not rendered empty, when the contract carries no billing
          amount and frequency (§4.2). Gated on the data rather than on billing_method,
          since only a subscription ever has these filled in.
      */}
      {period && (
        <div className="mon-panel mon-panel--period">
          <h4 className="mon-panel__head">
            Current Billing Period
            <span className="mon-panel__tag">{period.label}</span>
          </h4>
          <div className="mon-panel__cards">
            <Kpi label="Period Budget" value={money(period.budget)} />
            <Kpi label="Period Spent" value={money(period.spent)} />
            {/* Negative is expected pacing, never an error state, and never clamped. */}
            <Kpi
              label="Period Remaining"
              value={money(period.remaining)}
              negative={period.remaining < 0}
            />
          </div>
          <p className="mon-panel__foot">
            The period is the replenish amount and frequency on the contract's subscription
            budget, cycling from that budget's start date. Spend can exceed the period
            allotment while the campaign overall is healthy; that is pacing, not an error.
          </p>
        </div>
      )}

      {/*
          Rachel: "Current Billing Period Panel not present."

          Not a bug -- the panel is gated on the contract carrying a billing amount and
          frequency, and hers carries neither. But rendering nothing left her to guess
          between "hidden on purpose", "broken" and "no data yet". The ticket says omit
          the panel for PFR and Flat Fee, and that still holds: those genuinely have no
          amount to pace against. This is the third case, a subscription nobody has
          filled in, and it is fixable by whoever reads it.
      */}
      {!period && panels.billing_period_configurable && (
        <div className="mon-panel mon-panel--empty">
          <h4 className="mon-panel__head">Current Billing Period</h4>
          <p className="mon-panel__foot">
            This contract has no subscription budget with a replenish amount and frequency,
            so there is no period to pace against. Set them on the contract's Budget and
            this panel fills in.
          </p>
        </div>
      )}

      <div className="mon-panel__cards mon-panel__cards--loose">
        <Kpi
          label="Spend Per User"
          value={perUser.spendPerUser === null ? NO_VALUE : money(perUser.spendPerUser)}
        />
        <Kpi
          label="Impressions Per User"
          value={
            perUser.impressionsPerUser === null
              ? NO_VALUE
              : ratio(perUser.impressionsPerUser, 1)
          }
        />
      </div>
      <p className="mon-panel__foot">
        Per-user metrics divide the selected date range's spend by the campaign's selected
        audience: the whole list we uploaded, not the subset the networks matched. Selected
        is a current count rather than a windowed one.
      </p>
    </>
  )
}
